import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format Nepali Rupees
export function formatNPR(amount) {
  return `रु ${amount.toLocaleString("en-IN")}`;
}

// Stock is a float, because brass moves by the kilo and wire by the metre. Most
// of it is still whole pieces though, so "3" should read as 3 and not 3.0 -- and
// floating point arithmetic must not be allowed to surface as 0.30000000000000004.
export function formatQty(qty) {
  const n = Number(qty) || 0;
  return String(Math.round(n * 1000) / 1000);
}

/**
 * Render a product's size the way the shop writes it.
 *
 * Nepali puts the label in front of the number for some units and after it for
 * others -- "कराई नं. १४" but "कुकर २ लिटर" -- so the size unit record carries
 * its own position and this follows it.
 *
 * `sizeUnits` is the list from /api/size-units, keyed by id.
 */
export function formatSize(product, sizeUnits = {}) {
  if (product?.size_value === null || product?.size_value === undefined) return "";
  const n = formatQty(product.size_value);
  const su = sizeUnits[product.size_unit] || null;
  if (!su) return n;
  const label = su.name_np || su.name_en || "";
  return su.position === "prefix" ? `${label} ${n}` : `${n} ${label}`;
}

// Gauge is sheet thickness, and it runs backwards: 20 gauge is thicker, heavier
// and dearer than 24. Labelled rather than shown bare so nobody reads it as
// another size.
export function formatGauge(gauge) {
  if (gauge === null || gauge === undefined || gauge === "") return "";
  return `${formatQty(gauge)} गेज`;
}

// Format date for display
export function formatDate(date, includeTime = false) {
  const d = new Date(date);
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(includeTime && { hour: "2-digit", minute: "2-digit" }),
  };
  return d.toLocaleDateString("en-US", options);
}

// Format time only
export function formatTime(date) {
  return new Date(date).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Legacy category and location constants (for backward compatibility)
// Components should fetch from API instead
export const CATEGORIES = {
  steel: {
    name_en: "Steel Utensils",
    name_np: "स्टिल भाँडा",
    icon: "pot-steaming",
    color: "#6B7280",
  },
  brass: {
    name_en: "Brass & Religious",
    name_np: "पीतल/पूजा",
    icon: "lamp",
    color: "#D4AF37",
  },
  plastic: {
    name_en: "Plastic Items",
    name_np: "प्लास्टिक",
    icon: "cup-soda",
    color: "#3B82F6",
  },
  electric: {
    name_en: "Electric Items",
    name_np: "बिजुली",
    icon: "zap",
    color: "#F59E0B",
  },
  cleaning: {
    name_en: "Cleaning Tools",
    name_np: "सफाई",
    icon: "brush",
    color: "#10B981",
  },
  boxed: {
    name_en: "Boxed Items",
    name_np: "बक्स",
    icon: "package",
    color: "#8B5CF6",
  },
  other: {
    name_en: "Other Items",
    name_np: "अन्य",
    icon: "grid-3x3",
    color: "#6B7280",
  },
};

export const LOCATIONS = {
  hanging: { name_en: "Hanging", name_np: "झुण्डिएको" },
  shelf_top: { name_en: "Top Shelf", name_np: "माथि शेल्फ" },
  shelf_bottom: { name_en: "Bottom Shelf", name_np: "तल शेल्फ" },
  front_display: { name_en: "Front Display", name_np: "अगाडि" },
  storage: { name_en: "Storage Room", name_np: "गोदाम" },
  counter: { name_en: "Counter", name_np: "काउन्टर" },
};
